import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { useEntrance } from '@/components/motion/reveal'
import { SplitWords } from '@/components/motion/split-text'
import WaterRipple from '@/components/webgl/water-ripple'
import Button from '@/components/generals/button'
import Img from '@/components/generals/img'
import { media } from '@/constants/media'

const LETTERS = ['E', 'L', 'I', 'O']

const Hero: FC = (): ReactNode => {
    const root = useRef<HTMLElement>(null)

    useEntrance(root, timeline => {
        timeline
            .from('[data-hero-surface]', { scale: 1.18, duration: 2.4, ease: 'expo.out' }, 0)
            .from('[data-hero-letter]', { yPercent: 140, duration: 1.5, stagger: 0.09, ease: 'expo.out' }, 0.05)
            .from('[data-hero-meta]', { autoAlpha: 0, y: 14, duration: 1, stagger: 0.1 }, 0.35)
            .from('[data-hero-line]', { scaleX: 0, transformOrigin: '0% 50%', duration: 1.6, ease: 'expo.inOut' }, 0.2)
            .from('[data-hero-tagline] .mask > span', { yPercent: 140, duration: 1.2, stagger: 0.05 }, 0.55)
            .from('[data-hero-intro]', { autoAlpha: 0, y: 24, filter: 'blur(8px)', duration: 1.2, clearProps: 'filter' }, 0.75)
            .from('[data-hero-cta]', { autoAlpha: 0, y: 24, duration: 1 }, 0.9)
    })

    return (
        <section
            id='hero'
            ref={root}
            className='relative flex min-h-[100svh] flex-col justify-end overflow-hidden pb-20 pt-[var(--header-height)] md:pb-24'
        >
            <div data-hero-surface className='absolute inset-0'>
                <Img src={media.hoodedCoastStorm} alt='' preload sizes='100vw' className='opacity-70' />

                <WaterRipple image={media.hoodedCoastStorm} className='absolute inset-0' />
            </div>

            <div aria-hidden='true' className='pointer-events-none absolute inset-0 bg-linear-to-t from-ink via-ink/55 to-ink/10' />

            <div className='pointer-events-none relative gutter'>
                <div className='flex items-center justify-between gap-4 label-mono'>
                    <span data-hero-meta>
                        (SS26) — Essentials 04
                    </span>

                    <span data-hero-meta className='hidden text-acid sm:inline'>
                        Touch the water ↓
                    </span>

                    <span data-hero-meta className='hidden text-right md:inline'>
                        52.5200° N — 13.4050° E
                    </span>
                </div>

                <div data-hero-line aria-hidden='true' className='mt-5 h-px bg-line' />

                <div className='mt-8 grid gap-10 md:mt-10 lg:grid-cols-12 lg:items-end lg:gap-8'>
                    <h1 className='flex font-heading text-[36vw] uppercase leading-[0.78] tracking-[-0.02em] text-bone lg:col-span-7 lg:text-[21vw]'>
                        <span className='sr-only'>
                            Elio
                        </span>

                        {LETTERS.map(letter => (
                            <span key={letter} aria-hidden='true' className='mask'>
                                <span data-hero-letter>
                                    {letter}
                                </span>
                            </span>
                        ))}
                    </h1>

                    <div className='pointer-events-auto flex flex-col gap-6 lg:col-span-4 lg:col-start-9 lg:pb-3'>
                        <p data-hero-tagline className='serif-accent'>
                            <SplitWords text='Essentials for the unordinary.' />
                        </p>

                        <p data-hero-intro className='max-w-[38ch] body-copy text-bone/80'>
                            Technical outerwear, heavy knits and raw denim, cut in numbered runs of 300 and repaired for life.
                        </p>

                        <div data-hero-cta>
                            <Button href='/shop'>
                                Shop the drop
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default Hero
